import { Save, RotateCcw, Palette, Building2, Image as ImageIcon, SlidersHorizontal, Megaphone, X, Upload, Scissors, Store, ShoppingBag, Home as HomeIcon, Star, Heart, Crown, Zap, Coffee, Stethoscope, AlertCircle } from "lucide-react";
import "./personalizacion.css";

const availableIcons = [
  { name: 'Scissors', icon: Scissors, label: 'Tijeras' },
  { name: 'Store', icon: Store, label: 'Tienda' },
  { name: 'ShoppingBag', icon: ShoppingBag, label: 'Bolsa' },
  { name: 'Home', icon: HomeIcon, label: 'Casa' },
  { name: 'Star', icon: Star, label: 'Estrella' },
  { name: 'Heart', icon: Heart, label: 'Corazón' },
  { name: 'Crown', icon: Crown, label: 'Corona' },
  { name: 'Zap', icon: Zap, label: 'Rayo' },
  { name: 'Coffee', icon: Coffee, label: 'Café' },
  { name: 'Stethoscope', icon: Stethoscope, label: 'Salud' },
];

const PRESETS = [
  { name: 'Azul', primary: '#0d6efd', dark: '#0056b3' },
  { name: 'Verde', primary: '#28a745', dark: '#1e7e34' },
  { name: 'Púrpura', primary: '#6f42c1', dark: '#59359a' },
  { name: 'Naranja', primary: '#fd7e14', dark: '#e8590c' },
  { name: 'Rosa', primary: '#e83e8c', dark: '#d91a72' },
  { name: 'Turquesa', primary: '#20c997', dark: '#198754' },
  { name: 'Negro', primary: '#212529', dark: '#000000' },
  { name: 'Rojo', primary: '#dc3545', dark: '#b02a37' },
];

const isHex = (v) => /^#[0-9a-f]{6}$/i.test(v || "");

/**
 * Personalización: color de marca (el panel y la página de agenda lo toman al vivo),
 * datos del negocio, logo, preferencias de agenda y aviso a clientes. Todo se guarda
 * con un solo botón (handleSaveBrand manda colores + marca + preferencias).
 */
function PersonalizationTab({ customColors, updateColors, brandSettings, setBrandSettings, updateBrandSettings, resetToDefault, handleSaveBrand, handleImageUpload, services, clientData }) {
  const set = (patch) => updateBrandSettings({ ...brandSettings, ...patch });
  const setPref = (patch) => setBrandSettings((bs) => ({ ...bs, ...patch }));
  const setColor = (key, value) => { if (isHex(value)) updateColors({ ...customColors, [key]: value }); };
  const hasAdvance = services?.some((s) => s.requiresAdvance);
  const SelectedIcon = availableIcons.find((i) => i.name === brandSettings.selectedIcon)?.icon || Scissors;
  const announcement = brandSettings.announcement || {};

  return (
    <section className="cp-perso">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title">Personalización</h2>
          <p className="cp-page-sub">Cómo se ven tu página de agenda y este panel.</p>
        </div>
        <div className="cp-page-actions">
          <button type="button" className="cp-btn cp-btn-primary" onClick={handleSaveBrand}><Save size={16} /> Guardar</button>
        </div>
      </header>

      <div className="cp-perso-grid">
        <div className="cp-perso-col">
          {/* Color */}
          <article className="cp-card cp-perso-card" data-tour="perso.colors">
            <div className="cp-card-head">
              <h3 className="cp-card-title"><Palette size={18} /> Color de marca</h3>
              <button type="button" className="cp-linkbtn" onClick={resetToDefault}><RotateCcw size={14} /> Restablecer</button>
            </div>
            <p className="cp-muted">Se aplica a tu página de agenda y a este panel al momento.</p>
            <div className="cp-swatches cp-perso-presets" role="group" aria-label="Colores sugeridos">
              {PRESETS.map((p) => (
                <button key={p.name} type="button" className={`cp-swatch ${customColors.primaryColor?.toLowerCase() === p.primary ? "is-active" : ""}`} style={{ background: p.primary }} title={p.name} aria-label={p.name} onClick={() => updateColors({ primaryColor: p.primary, primaryDark: p.dark })} />
              ))}
            </div>
            <div className="cp-grid-2">
              <label className="cp-field">
                <span>Color principal</span>
                <span className="cp-perso-colorfield">
                  <input type="color" value={isHex(customColors.primaryColor) ? customColors.primaryColor : "#3b82f6"} onChange={(e) => setColor("primaryColor", e.target.value)} aria-label="Elegir color principal" />
                  <input type="text" className="cp-input" value={customColors.primaryColor || ""} onChange={(e) => updateColors({ ...customColors, primaryColor: e.target.value })} placeholder="#0d6efd" spellCheck={false} />
                </span>
              </label>
              <label className="cp-field">
                <span>Tono oscuro</span>
                <span className="cp-perso-colorfield">
                  <input type="color" value={isHex(customColors.primaryDark) ? customColors.primaryDark : "#2563eb"} onChange={(e) => setColor("primaryDark", e.target.value)} aria-label="Elegir tono oscuro" />
                  <input type="text" className="cp-input" value={customColors.primaryDark || ""} onChange={(e) => updateColors({ ...customColors, primaryDark: e.target.value })} placeholder="#0056b3" spellCheck={false} />
                </span>
                <small>Para botones presionados y detalles.</small>
              </label>
            </div>
            <div className="cp-perso-preview" aria-hidden="true">
              <span className="cp-btn cp-btn-primary">Agendar cita</span>
              <span className="cp-btn">Ver servicios</span>
              <span className="cp-pill cp-pill-accent">Hoy</span>
            </div>
          </article>

          {/* Logo */}
          <article className="cp-card cp-perso-card" data-tour="perso.logo">
            <div className="cp-card-head"><h3 className="cp-card-title"><ImageIcon size={18} /> Logo</h3></div>
            <div className="cp-perso-brandpreview">
              {brandSettings.logoType === 'image' && brandSettings.logoImage
                ? <img src={brandSettings.logoImage} alt="" />
                : <SelectedIcon size={26} className="cp-perso-brandicon" />}
              <span>
                <b>{brandSettings.businessName || "Tu negocio"}</b>
                {brandSettings.slogan && <small>{brandSettings.slogan}</small>}
              </span>
            </div>
            <div className="cp-segment cp-segment-fill" role="tablist" aria-label="Tipo de logo">
              <button type="button" role="tab" aria-selected={brandSettings.logoType !== 'image'} className={`cp-segment-btn ${brandSettings.logoType !== 'image' ? "is-active" : ""}`} onClick={() => set({ logoType: 'icon' })}>Ícono</button>
              <button type="button" role="tab" aria-selected={brandSettings.logoType === 'image'} className={`cp-segment-btn ${brandSettings.logoType === 'image' ? "is-active" : ""}`} onClick={() => set({ logoType: 'image' })}>Mi imagen</button>
            </div>
            {brandSettings.logoType === 'image' ? (
              <div className="cp-perso-upload">
                <label className="cp-btn">
                  <Upload size={16} /> {brandSettings.logoImage ? "Cambiar imagen" : "Subir imagen"}
                  <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
                </label>
                {brandSettings.logoImage && (
                  <button type="button" className="cp-btn cp-btn-danger" onClick={() => set({ logoImage: null, logoFile: null, logoType: 'icon', selectedIcon: 'Scissors' })}><X size={16} /> Quitar</button>
                )}
                <small className="cp-hint">JPG, PNG o SVG. Ideal 200 × 60 px. También es el ícono de la app cuando la instalan.</small>
              </div>
            ) : (
              <div className="cp-perso-icons" role="radiogroup" aria-label="Ícono">
                {availableIcons.map(({ name, icon: Icon, label }) => (
                  <button key={name} type="button" role="radio" aria-checked={brandSettings.selectedIcon === name} className={`cp-choice ${brandSettings.selectedIcon === name ? "is-active" : ""}`} onClick={() => set({ selectedIcon: name })}>
                    <Icon size={20} /><small>{label}</small>
                  </button>
                ))}
              </div>
            )}
          </article>
        </div>

        <div className="cp-perso-col">
          {/* Datos del negocio */}
          <article className="cp-card cp-perso-card" data-tour="perso.brand">
            <div className="cp-card-head"><h3 className="cp-card-title"><Building2 size={18} /> Datos del negocio</h3></div>
            <p className="cp-muted">Es lo que ven tus clientes en tu página de agenda.</p>
            <div className="cp-form">
              <label className="cp-field"><span>Nombre</span><input type="text" className="cp-input" value={brandSettings.businessName || ""} onChange={(e) => set({ businessName: e.target.value })} placeholder="Barbería El Corte" /></label>
              <label className="cp-field"><span>Slogan <small>(opcional)</small></span><input type="text" className="cp-input" value={brandSettings.slogan || ""} onChange={(e) => set({ slogan: e.target.value })} placeholder="Tu estilo, nuestra pasión" /></label>
              <div className="cp-grid-2">
                <label className="cp-field"><span>Teléfono</span><input type="tel" className="cp-input" value={brandSettings.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="951 123 4567" /><small>Varios, separados por coma.</small></label>
                <label className="cp-field"><span>Correo</span><input type="email" className="cp-input" value={brandSettings.email || ""} onChange={(e) => set({ email: e.target.value })} placeholder="hola@tunegocio.com" /></label>
              </div>
              <label className="cp-field"><span>Dirección</span><input type="text" className="cp-input" value={brandSettings.address || ""} onChange={(e) => set({ address: e.target.value })} placeholder="Calle 1 #23, Centro" /></label>
              <label className="cp-field">
                <span>Link de Google Maps</span>
                <input type="url" className="cp-input" value={brandSettings.googleMapsLink || ""} onChange={(e) => set({ googleMapsLink: e.target.value })} placeholder="https://maps.app.goo.gl/…" />
                <small>Desde Google Maps: Compartir › Copiar link. <a href="https://support.google.com/business/answer/3038177" target="_blank" rel="noopener noreferrer">¿Cómo?</a></small>
              </label>
            </div>
          </article>

          {/* Preferencias de agenda */}
          <article className="cp-card cp-perso-card" data-tour="perso.agenda">
            <div className="cp-card-head"><h3 className="cp-card-title"><SlidersHorizontal size={18} /> Agenda</h3></div>
            <div className="cp-switch">
              <div>
                <div className="cp-switch-label">Pedir confirmación por WhatsApp</div>
                <div className="cp-switch-hint">
                  {hasAdvance
                    ? "Con anticipo, el cliente confirma pagando. Sin anticipo, recibe un mensaje y tiene 5 minutos para confirmar."
                    : "El cliente recibe un mensaje y tiene 5 minutos para confirmar; si no, el lugar se libera."}
                </div>
              </div>
              <button type="button" role="switch" aria-checked={!!brandSettings.confirmWebAppointment} className="cp-toggle" onClick={() => setPref({ confirmWebAppointment: !brandSettings.confirmWebAppointment })} aria-label="Pedir confirmación por WhatsApp" />
            </div>
            {hasAdvance && brandSettings.confirmWebAppointment && <div className="cp-note"><AlertCircle size={16} /><div>Tienes servicios con anticipo: en esos, el comprobante es la confirmación.</div></div>}
            <div className="cp-switch">
              <div>
                <div className="cp-switch-label">Formato de 24 horas</div>
                <div className="cp-switch-hint">{brandSettings.format24Hours ? "Las horas se ven como 14:30." : "Las horas se ven como 2:30 p.m."}</div>
              </div>
              <button type="button" role="switch" aria-checked={!!brandSettings.format24Hours} className="cp-toggle" onClick={() => setPref({ format24Hours: !brandSettings.format24Hours })} aria-label="Formato de 24 horas" />
            </div>
            {clientData?.staff && clientData.staff.length > 1 && (
              <label className="cp-field">
                <span>Cómo llamas a tu equipo</span>
                <input type="text" className="cp-input" value={brandSettings.staffLabel || ""} maxLength={30} placeholder="Profesional" onChange={(e) => setPref({ staffLabel: e.target.value })} />
                <small>En singular: Barbero, Doctora, Estilista… Es lo que ve el cliente al elegir con quién agendar.</small>
              </label>
            )}
          </article>

          {/* Aviso */}
          <article className="cp-card cp-perso-card" data-tour="perso.aviso">
            <div className="cp-switch">
              <div>
                <div className="cp-switch-label"><Megaphone size={16} className="cp-perso-inline" /> Aviso a tus clientes</div>
                <div className="cp-switch-hint">Un recuadro al entrar a tu página: horario especial, promoción, vacaciones. Lo cierran y siguen agendando.</div>
              </div>
              <button type="button" role="switch" aria-checked={!!announcement.enabled} className="cp-toggle" onClick={() => setPref({ announcement: { ...announcement, enabled: !announcement.enabled } })} aria-label="Mostrar aviso" />
            </div>
            {announcement.enabled && (
              <div className="cp-form">
                <label className="cp-field"><span>Título <small>(opcional)</small></span><input type="text" className="cp-input" maxLength={60} placeholder="Horario especial" value={announcement.title || ""} onChange={(e) => setPref({ announcement: { ...announcement, title: e.target.value } })} /></label>
                <label className="cp-field">
                  <span>Mensaje</span>
                  <textarea className="cp-input" rows={3} maxLength={500} placeholder="Este sábado abrimos de 9 a 2. ¡Aparta tu lugar!" value={announcement.message || ""} onChange={(e) => setPref({ announcement: { ...announcement, message: e.target.value } })} />
                  <small>{(announcement.message || "").length}/500</small>
                </label>
              </div>
            )}
          </article>
        </div>
      </div>

      <div className="cp-perso-save">
        <button type="button" className="cp-btn cp-btn-primary cp-btn-block" onClick={handleSaveBrand}><Save size={16} /> Guardar cambios</button>
      </div>
    </section>
  );
}

export default PersonalizationTab;
